import { collection, addDoc, doc, writeBatch, onSnapshot, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseDDH';
import { normalizeEmail } from './clients';

const COLLECTION = 'messages';

// serverTimestamp() reads back as null until the server confirms the write, so
// a just-sent message would sort as the oldest possible and visibly jump into
// place a moment later. 'estimate' fills in the local clock in the meantime.
const ESTIMATE = { serverTimestamps: 'estimate' };

export const MAX_BODY = 4000;

// One continuous conversation per client, keyed by email like quotes are.
// Sorted in JS for the same reason: a where() plus an orderBy() on a different
// field would need a composite index, and threads here are short.
function sortByCreated(docs) {
  return docs.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
}

export function watchConversation(clientEmail, onData, onError) {
  const q = query(collection(db, COLLECTION), where('clientEmail', '==', normalizeEmail(clientEmail)));
  return onSnapshot(
    q,
    (snapshot) => onData(sortByCreated(snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) })))),
    onError,
  );
}

// Admin-side: everything a client has sent that hasn't been read yet, across
// all clients, so the Messages tab can carry a count.
export function watchUnreadForAdmin(onData, onError) {
  const q = query(collection(db, COLLECTION), where('readByAdmin', '==', false));
  return onSnapshot(
    q,
    (snapshot) => onData(snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) }))),
    onError,
  );
}

export function watchUnreadForClient(clientEmail, onData, onError) {
  const q = query(
    collection(db, COLLECTION),
    where('clientEmail', '==', normalizeEmail(clientEmail)),
    where('readByClient', '==', false),
  );
  return onSnapshot(
    q,
    (snapshot) => onData(snapshot.docs.map((d) => ({ id: d.id, ...d.data(ESTIMATE) }))),
    onError,
  );
}

// `from` decides which side counts it as unread: whoever wrote it has, by
// definition, already read it. firestore.rules enforces that pairing so a
// client can't post a message that claims to be from us.
export async function sendMessage({ clientEmail, from, body, authorEmail }) {
  return addDoc(collection(db, COLLECTION), {
    clientEmail: normalizeEmail(clientEmail),
    from,
    body: body.trim().slice(0, MAX_BODY),
    authorEmail,
    createdAt: serverTimestamp(),
    readByAdmin: from === 'admin',
    readByClient: from === 'client',
  });
}

// Batched: opening a thread with twenty unread messages would otherwise fire
// twenty separate writes, each triggering its own snapshot round-trip. One
// batch commits them together, and does nothing at all when there's nothing
// to mark.
function markRead(ids, field) {
  if (!ids.length) return Promise.resolve();
  const batch = writeBatch(db);
  ids.forEach((id) => batch.update(doc(db, COLLECTION, id), { [field]: true }));
  return batch.commit();
}

export function markAllReadByAdmin(ids) {
  return markRead(ids, 'readByAdmin');
}

export function markAllReadByClient(ids) {
  return markRead(ids, 'readByClient');
}

export function formatMessageTime(ts) {
  if (!ts) return '';
  return ts.toDate().toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
